// Chat Component - Conversation Interface
// Phase 6: With Voice Features (Gemini TTS + Speech Recognition)
// © 2025 precatio, llc

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import './Chat.css';

// Tier check for voice features
const VOICE_ENABLED_TIERS = ['companion', 'retreatant'];

// Cached audio files for greetings by conversation type (no API call needed)
const CACHED_GREETING_AUDIO = {
  examen: '/audio/greeting-examen.wav',
  novena: '/audio/greeting-novena.wav',
  prayers: '/audio/greeting-prayers.wav',
  askiggy: '/audio/greeting-askiggy.wav',
  discernment: '/audio/greeting-discernment.wav',
  gospelprayer: '/audio/greeting-gospelprayer.wav'
};

// Split text into sentences for chunked TTS
const splitIntoChunks = (text) => {
  // Split on sentence endings, keeping the punctuation
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  
  // Group into chunks of 1-2 sentences (~100-150 chars each)
  const chunks = [];
  let currentChunk = '';
  
  for (const sentence of sentences) {
    const trimmed = sentence.trim();
    if (!trimmed) continue;
    
    if (currentChunk && (currentChunk + ' ' + trimmed).length < 150) {
      currentChunk += ' ' + trimmed;
    } else {
      if (currentChunk) chunks.push(currentChunk);
      currentChunk = trimmed;
    }
  }
  
  if (currentChunk) chunks.push(currentChunk);
  
  return chunks.length > 0 ? chunks : [text];
};

const Chat = ({ conversationType, resumeSessionId, onBack, onShowSubscription }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => 
    resumeSessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const [isFirstVisit, setIsFirstVisit] = useState(!resumeSessionId);
  const [progressStep, setProgressStep] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(!!resumeSessionId);
  
  // Voice state - default ON for users with access, persisted in localStorage
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [voiceInitialized, setVoiceInitialized] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioQueue, setAudioQueue] = useState([]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);

  const { token, isAuthenticated, linkConversation, user } = useAuth();
  
  // Check if user has voice access
  const hasVoiceAccess = user?.subscriptionTier && VOICE_ENABLED_TIERS.includes(user.subscriptionTier);

  // Initialize voice preference from localStorage, default ON for users with access
  useEffect(() => {
    if (hasVoiceAccess) {
      const savedPref = localStorage.getItem('precat_voice_enabled');
      // Default to ON if no preference saved, otherwise use saved preference
      const shouldEnable = savedPref === null ? true : savedPref === 'true';
      setIsVoiceEnabled(shouldEnable);
    } else {
      setIsVoiceEnabled(false);
    }
    setVoiceInitialized(true);
  }, [hasVoiceAccess]);

  // Save voice preference to localStorage when it changes
  useEffect(() => {
    if (hasVoiceAccess) {
      localStorage.setItem('precat_voice_enabled', isVoiceEnabled.toString());
    }
  }, [isVoiceEnabled, hasVoiceAccess]);

  const conversationTitles = {
    examen: 'Daily Examen',
    novena: 'Custom Novena',
    prayers: 'Common Prayers',
    askiggy: 'Ask Iggy',
    discernment: 'Discernment',
    gospelprayer: 'Gospel Prayer'
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // If resuming a conversation, load the history
    if (resumeSessionId) {
      loadConversationHistory();
    } else {
      // New conversation - show greeting
      sendGreeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationType, resumeSessionId]);

  // Link conversation to user account if authenticated
  useEffect(() => {
    if (isAuthenticated && sessionId && !resumeSessionId) {
      linkConversation(sessionId);
    }
  }, [isAuthenticated, sessionId, linkConversation, resumeSessionId]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInput(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Play audio queue
  useEffect(() => {
    if (audioQueue.length > 0 && !isPlayingAudio && isVoiceEnabled) {
      playNextAudio();
    }
  }, [audioQueue, isPlayingAudio, isVoiceEnabled]);

  const playNextAudio = useCallback(() => {
    if (audioQueue.length === 0) return;
    
    const audioUrl = audioQueue[0];
    setIsPlayingAudio(true);
    
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(err => {
        console.error('Audio playback error:', err);
        setIsPlayingAudio(false);
        setAudioQueue(prev => prev.slice(1));
      });
    }
  }, [audioQueue]);

  const handleAudioEnded = () => {
    setIsPlayingAudio(false);
    // Only revoke blob URLs (not static file URLs)
    if (audioRef.current?.src && audioRef.current.src.startsWith('blob:')) {
      URL.revokeObjectURL(audioRef.current.src);
    }
    setAudioQueue(prev => prev.slice(1));
  };

  const loadConversationHistory = async () => {
    try {
      const response = await fetch(`/api/conversation/${resumeSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation');
      }

      const data = await response.json();
      
      if (data.history && data.history.length > 0) {
        // Convert database format to message format
        const loadedMessages = data.history.map(msg => ({
          role: msg.message_role,
          content: msg.message_content,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(loadedMessages);
      } else {
        // No history found, show greeting
        sendGreeting();
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
      // Fall back to greeting
      sendGreeting();
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const sendGreeting = () => {
    const greetings = {
      examen: "Peace be with you. I'm Iggy, here to guide you through the Daily Examen—a time to notice where God was present in your day.\n\nWhen you're ready, let's begin: What are you grateful for today?",
      
      novena: "Peace be with you. I'm Iggy, and I'm here to help you create a custom novena—a 9-day prayer journey for something on your heart.\n\nWhat would you like to pray about for the next 9 days?",
      
      prayers: "Welcome! I'd love to pray with you. Which prayer would you like to pray together?\n\n1. Our Father\n2. Hail Mary\n3. Glory Be\n4. Act of Contrition\n5. Suscipe (St. Ignatius)\n6. Prayer for Generosity\n7. Anima Christi\n8. The Slow Work of God (Teilhard de Chardin)\n\nJust let me know which one speaks to you right now.",
      
      askiggy: "Peace be with you. I'm Iggy, your spiritual companion. I'm here to talk with you about whatever is on your heart today.\n\nWhat would you like to talk about?",
      
      discernment: "Peace be with you. I'm Iggy, and I'll guide you through Ignatian discernment to help you listen for God's voice in a decision you're facing.\n\nWhat decision are you trying to make?",
      
      gospelprayer: "Peace be with you. I'm Iggy, and today I'll guide you through imaginative prayer with today's Gospel. We'll place ourselves in the Scripture scene together, engaging all your senses.\n\nAre you ready to begin?"
    };

    const greeting = greetings[conversationType] || greetings.examen;
    
    setMessages([{
      role: 'assistant',
      content: greeting,
      timestamp: new Date()
    }]);
    
    setIsFirstVisit(false);
  };

  // Speak greeting once voice is initialized (separate effect to handle timing)
  useEffect(() => {
    if (voiceInitialized && isVoiceEnabled && hasVoiceAccess && messages.length === 1 && messages[0].role === 'assistant' && !resumeSessionId) {
      // For greetings, use cached audio file by conversation type
      const cachedAudioUrl = CACHED_GREETING_AUDIO[conversationType];
      if (cachedAudioUrl) {
        setAudioQueue(prev => [...prev, cachedAudioUrl]);
      } else {
        // Fallback to TTS API if no cached audio (unlikely)
        fetchAudioForText(messages[0].content).then(url => {
          if (url) setAudioQueue(prev => [...prev, url]);
        });
      }
    }
  }, [voiceInitialized, isVoiceEnabled, hasVoiceAccess, messages, resumeSessionId, conversationType]);

  // Fetch audio for a single chunk of text
  const fetchSingleAudio = async (text) => {
    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('TTS error:', response.status, errorData);
        return null;
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBlob = new Blob([arrayBuffer], { type: 'audio/wav' });
      return URL.createObjectURL(audioBlob);
    } catch (error) {
      console.error('Error fetching TTS:', error);
      return null;
    }
  };

  // Fetch audio for text with chunking
  // Starts ALL chunks in parallel, returns when first chunk is ready
  // Remaining chunks queue automatically as they complete
  const fetchAudioWithChunking = async (text) => {
    if (!hasVoiceAccess || !isVoiceEnabled || !token) return null;
    
    const chunks = splitIntoChunks(text);
    
    if (chunks.length === 0) return null;
    
    // Start ALL chunk requests in parallel immediately
    const chunkPromises = chunks.map(chunk => fetchSingleAudio(chunk));
    
    // Wait for first chunk
    const firstChunkUrl = await chunkPromises[0];
    
    // Queue remaining chunks as they complete (in order)
    if (chunks.length > 1) {
      Promise.all(chunkPromises.slice(1))
        .then(urls => {
          urls.forEach(url => {
            if (url) {
              setAudioQueue(prev => [...prev, url]);
            }
          });
        });
    }
    
    return firstChunkUrl;
  };

  // Simple fetch for fallback (greeting without cache)
  const fetchAudioForText = async (text) => {
    if (!hasVoiceAccess || !isVoiceEnabled || !token) return null;
    return fetchSingleAudio(text);
  };

  const extractProgressStep = (content) => {
    // Look for progress indicators like "✨ Examen: Step 2 of 5 ✨"
    const match = content.match(/[✨🙏]\s*Examen:\s*Step\s*(\d+)\s*of\s*(\d+)\s*[✨🙏]/i);
    if (match) {
      return {
        current: parseInt(match[1]),
        total: parseInt(match[2])
      };
    }
    return null;
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Build headers with auth token if available
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: input,
          sessionId: sessionId,
          conversationType: conversationType,
          conversationHistory: messages
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle premium feature access error
        if (response.status === 401 || response.status === 403) {
          throw new Error(errorData.message || 'Access denied');
        }
        
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        crisisDetected: data.crisisDetected,
        timestamp: new Date()
      };

      // Extract progress if this is an Examen
      if (conversationType === 'examen') {
        const step = extractProgressStep(data.message);
        if (step) {
          setProgressStep(step);
        }
      }

      // If voice is enabled, wait for first audio chunk before showing text
      if (isVoiceEnabled && hasVoiceAccess && token) {
        // Fetch first chunk (remaining chunks load in background)
        const firstAudioUrl = await fetchAudioWithChunking(data.message);
        
        // Now show text and start audio together
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        
        if (firstAudioUrl) {
          setAudioQueue(prev => [...prev, firstAudioUrl]);
        }
      } else {
        // No voice - show text immediately
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: error.message || "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    } finally {
      // Refocus the input after response
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleBackClick = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setAudioQueue([]);
    
    // Signal to backend that conversation has ended (triggers profile update)
    if (token && sessionId) {
      fetch('/api/conversation/end', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId })
      }).catch(err => console.error('Error ending conversation:', err));
    }
    onBack();
  };

  const toggleVoice = () => {
    if (!hasVoiceAccess) {
      // Show subscription modal
      if (onShowSubscription) {
        onShowSubscription();
      }
      return;
    }
    
    const newValue = !isVoiceEnabled;
    setIsVoiceEnabled(newValue);
    
    // Stop any playing audio if disabling
    if (isVoiceEnabled && audioRef.current) {
      audioRef.current.pause();
      setAudioQueue([]);
      setIsPlayingAudio(false);
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please try Chrome or Edge.');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      setInput('');
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioQueue([]);
    setIsPlayingAudio(false);
  };

  return (
    <div className="chat-container">
      {/* Hidden audio element for playback */}
      <audio 
        ref={audioRef} 
        onEnded={handleAudioEnded}
        onError={() => {
          setIsPlayingAudio(false);
          setAudioQueue(prev => prev.slice(1));
        }}
      />
      
      <div className="chat-header">
        <button className="back-button" onClick={handleBackClick} title="Back to home">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="#d4af37"/>
          </svg>
        </button>
        
        <div className="header-content">
          <img src="/iggy-icon.jpg" alt="Iggy" className="header-logo" />
          <div className="header-text">
            <h1 className="chat-title">{conversationTitles[conversationType]}</h1>
            {progressStep && conversationType === 'examen' && (
              <p className="progress-indicator">
                Step {progressStep.current} of {progressStep.total}
              </p>
            )}
          </div>
        </div>

        {/* Voice toggle button */}
        <button 
          className={`voice-toggle-btn ${isVoiceEnabled ? 'active' : ''} ${!hasVoiceAccess ? 'locked' : ''}`}
          onClick={toggleVoice}
          title={hasVoiceAccess 
            ? (isVoiceEnabled ? "Mute Iggy's voice" : "Enable Iggy's voice") 
            : "Upgrade to Companion for voice features"
          }
        >
          {isVoiceEnabled ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 18.04 14.83 18.45 14 18.7V20.76C15.38 20.45 16.63 19.81 17.69 18.95L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" fill="currentColor"/>
            </svg>
          )}
          {!hasVoiceAccess && (
            <span className="lock-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
            </span>
          )}
        </button>
      </div>

      <div className="messages-container">
        {isLoadingHistory && (
          <div className="message assistant-message">
            <div className="message-avatar">
              <img src="/iggy-icon.jpg" alt="Iggy" />
            </div>
            <div className="message-content loading">
              <p>Loading your conversation...</p>
            </div>
          </div>
        )}
        {!isLoadingHistory && messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            {message.role === 'assistant' && (
              <div className="message-avatar">
                <img src="/iggy-icon.jpg" alt="Iggy" />
              </div>
            )}
            <div className="message-content">
              {message.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message assistant-message">
            <div className="message-avatar">
              <img src="/iggy-icon.jpg" alt="Iggy" />
            </div>
            <div className="message-content loading">
              <span className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Audio playing indicator */}
      {isPlayingAudio && (
        <div className="audio-playing-indicator" onClick={stopAudio}>
          <div className="audio-waves">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span>Iggy is speaking... (tap to stop)</span>
        </div>
      )}

      <div className="input-container">
        {/* Microphone button */}
        <button 
          className={`mic-button ${isRecording ? 'recording' : ''}`}
          onClick={toggleRecording}
          title={isRecording ? "Stop recording" : "Start voice input"}
          disabled={isLoading}
        >
          {isRecording ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14ZM17.3 11C17.3 14 14.76 16.1 12 16.1C9.24 16.1 6.7 14 6.7 11H5C5 14.41 7.72 17.23 11 17.72V21H13V17.72C16.28 17.23 19 14.41 19 11H17.3Z" fill="currentColor"/>
            </svg>
          )}
        </button>
        
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isRecording ? "Listening..." : "Type your message..."}
          disabled={isLoading}
          rows="3"
        />
        <button 
          className="send-button"
          onClick={sendMessage} 
          disabled={isLoading || !input.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default Chat;
